import { useCallback, useState } from 'react';
import { apiClient } from '../index';
import { ClickReportExportPayload, ClickReportFilters, ClickReportResponse } from '../types';

const toQueryString = (filters: ClickReportFilters): Record<string, string> => {
  const out: Record<string, string> = {};
  if (filters.startDate) out.startDate = filters.startDate;
  if (filters.endDate) out.endDate = filters.endDate;
  if (filters.search) out.search = filters.search;
  if (filters.campaignId !== undefined && filters.campaignId !== null && filters.campaignId !== '') {
    out.campaignId = Array.isArray(filters.campaignId) ? filters.campaignId.join(',') : String(filters.campaignId);
  }
  if (filters.campaignTag) {
    out.campaignTag = Array.isArray(filters.campaignTag) ? filters.campaignTag.join(',') : filters.campaignTag;
  }
  if (filters.domain) {
    out.domain = Array.isArray(filters.domain) ? filters.domain.join(',') : filters.domain;
  }
  if (filters.page) out.page = String(filters.page);
  if (filters.limit) out.limit = String(filters.limit);
  if (filters.uniqueOnly) out.uniqueOnly = '1';
  return out;
};

export const useClickReport = () => {
  const [data, setData] = useState<ClickReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async (filters: ClickReportFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = toQueryString(filters);
      const result = await apiClient.get<ClickReportResponse>('/api/click-report', params);
      setData(result);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch click report';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const escape = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const exportCsv = useCallback(async (filters: ClickReportFilters) => {
    setExporting(true);
    setError(null);
    try {
      const params = toQueryString(filters);
      const query = new URLSearchParams(params).toString();
      const url = query ? `/api/click-report/export?${query}` : '/api/click-report/export';
      const payload = await apiClient.get<ClickReportExportPayload>(url);
      const lines = [payload.headers.map(escape).join(',')];
      for (const row of payload.rows) {
        lines.push(row.map(escape).join(','));
      }
      apiClient.triggerCsvDownload(payload.filename || 'click-report.csv', lines.join('\n'));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export click report';
      setError(message);
      throw err;
    } finally {
      setExporting(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    exporting,
    fetchReport,
    exportCsv,
  };
};
